import React, { useEffect, useState } from "react";
import { graphql, useMutation } from "react-relay";
import PropTypes from "prop-types";
import jwtDecode from "jwt-decode";
import getCookie from "../utils/getCookie";

// the default only exists to make unit tests very slightly easier to write
export const AuthContext = React.createContext({ user: {} });

/**
 * The AuthContext (provided by AuthContextProvider) is the core place for state &
 * functionality for authentication while signing *in* or signing *up*.
 *
 * The core piece of authentication data that JB works on is the "validated email
 * token", which is a token proving that a user controls an email address, verified
 * either with an email passcode or social signon.
 *
 * Once you set a validatedEmailToken (by passing it in or calling
 * setValidatedEmailToken), you can:
 * - call applyAccessKey and/or registerUser functions in this context
 * - creating a workspace? invoke the create_workspace mutation with the
 *   validatedEmailToken
 * - logging in to a workspace? send the browser to /auth-relay on the subdomain
 */
export const AuthContextProvider = ({
  validatedEmailToken,
  cookieDomain,
  children,
  accessKey,
  googleAuthUrl,
  linkedinAuthUrl
}) => {
  const [user, setUser] = useState({
    validatedEmailToken: null,
    email: null,
    firstName: null,
    lastName: null
  });

  const setValidatedEmailTokenCookie = validatedEmailToken => {
    document.cookie = `validated_email_token=${validatedEmailToken}; domain=${cookieDomain}; path=/`;
  };

  const setValidatedEmailToken = validatedEmailToken => {
    if (validatedEmailToken) {
      setValidatedEmailTokenCookie(validatedEmailToken);
      const decoded = jwtDecode(validatedEmailToken);
      setUser({
        validatedEmailToken,
        email: decoded.email,
        firstName: decoded.first_name,
        lastName: decoded.last_name
      });
    } else {
      setUser({
        validatedEmailToken: null,
        email: null,
        firstName: null,
        lastName: null
      });
    }
  };

  useEffect(() => {
    if (!validatedEmailToken) {
      const vEC = getCookie("validated_email_token");
      if (vEC) setValidatedEmailToken(vEC);
    } else {
      setValidatedEmailTokenCookie(validatedEmailToken);
      setValidatedEmailToken(validatedEmailToken);
    }
  }, [validatedEmailToken]);

  const [error, setError] = useState(null);

  const [hasExistingUserAccount, setHasExistingUserAccount] = useState(true);

  const [registerUserMutation] = useMutation(graphql`
    mutation AuthContextRegisterUserMutation($input: RegisterUserInput!) {
      register_user(input: $input) {
        user {
          __typename
          ... on InactiveAccessKey {
            message
          }
          ... on InvalidAccessKey {
            message
          }
          ... on AccessKeyConsumed {
            message
          }
          ... on AccessKeyRequired {
            message
          }
          ... on UserLimitReached {
            message
          }
          ... on EmailDomainNotAllowed {
            allowed_domains
            domain
          }
        }
      }
    }
  `);

  const registerUser = (firstName, lastName, onError, onCompleted) => {
    registerUserMutation({
      variables: {
        input: {
          validated_email_token: user.validatedEmailToken,
          first_name: firstName,
          last_name: lastName,
          access_key: accessKey.key
        }
      },
      onError,
      onCompleted: res => {
        const typename = res && res.register_user.user.__typename;
        if (typename !== "User") {
          setError(res.register_user.user);
        } else {
          onCompleted();
        }
      }
    });
  };

  const [applyAccessKeyMutation] = useMutation(graphql`
    mutation AuthContextApplyAccessKeyMutation($input: ApplyAccessKeyInput!) {
      apply_access_key(input: $input) {
        target_url {
          __typename
          ... on StringWrapper {
            value
          }
          ... on InactiveAccessKey {
            message
          }
          ... on InvalidAccessKey {
            message
          }
          ... on AccessKeyConsumed {
            message
          }
        }
      }
    }
  `);

  const applyAccessKey = onError => {
    // TODO: can I make one function that calls applyAccessKey and also implicitly calls
    // registerUser if the target_url is NULL? Is that always want we want when
    // applyAccessKey is called?
    applyAccessKeyMutation({
      variables: {
        input: {
          validated_email_token: user.validatedEmailToken,
          access_key: accessKey.key
        }
      },
      onError,
      onCompleted: res => {
        if (res && !res.apply_access_key.target_url) {
          setHasExistingUserAccount(false);
          return;
        }

        const typename = res.apply_access_key.target_url.__typename;
        if (typename !== "StringWrapper") {
          setError(res.apply_access_key.target_url);
        } else {
          const redirect = res.apply_access_key.target_url.value;
          if (redirect) {
            window.location.href = `/auth-relay?next=${redirect}`;
          }
        }
      }
    });
  };

  const resetStateVars = () => {
    setError(null);
    setHasExistingUserAccount(true);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setValidatedEmailToken,
        applyAccessKey,
        registerUser,
        hasExistingUserAccount,
        accessKey,
        googleAuthUrl,
        linkedinAuthUrl,
        resetStateVars,
        error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  validatedEmailToken: PropTypes.string,
  cookieDomain: PropTypes.string,
  accessKey: PropTypes.object,
  googleAuthUrl: PropTypes.string,
  linkedinAuthUrl: PropTypes.string,
  children: PropTypes.node
};
